import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, ChevronDown, Banknote } from '../../lib/icons';
import { toast } from 'sonner';
import { solderReception, solderCamion, etatPaiement, MODES_PAIEMENT, lignesDuBon, decompte, dejaCouvert, nomTiers } from '../../lib/dataProvider';
import { PrintDoc } from '../print/PrintDoc';
import { attribuerNumero } from '../../lib/souches';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';
import { caisseImposee } from '../../lib/permissions';
import { useTresorerieVerrou } from '../../lib/tresorerieEtat';

const STYLE = {
  impaye: 'border-red-300 bg-red-50 text-red-700',
  solde: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  depot: 'border-sky-300 bg-sky-50 text-sky-700',
  incomplet: 'border-slate-400 bg-slate-100 text-slate-700',
  attente: 'border-amber-300 bg-amber-50 text-amber-700',
  aucun: 'border-slate-200 bg-slate-50 text-slate-500',
};

export const PaiementBadge = ({ reception }) => {
  const e = etatPaiement(reception);
  return (
    <span data-testid={`paiement-badge-${reception.id}`} data-etat={e.code} className={`inline-block border px-1.5 py-0.5 font-data text-[11px] font-bold uppercase tracking-[0.06em] ${STYLE[e.code]}`}>
      {e.label}
    </span>
  );
};

// « Solder le camion » : toutes les lignes impayées d'un bon ventilé, en un clic.
export const SolderCamionButton = ({ reception, utilisateur, onDone }) => {
  const { user } = useAuth();
  const { verrou: tresorerieKo, titre: titreVerrou } = useTresorerieVerrou();
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [recu, setRecu] = useState(null);
  const ref = useRef(null);
  const impayees = lignesDuBon(reception).filter((l) => etatPaiement(l).code === 'impaye');
  const total = impayees.reduce((s, l) => s + decompte(l).montant - dejaCouvert(l), 0);

  useEffect(() => {
    if (!ouvert) return;
    const fermer = (e) => ref.current && !ref.current.contains(e.target) && setOuvert(false);
    document.addEventListener('mousedown', fermer);
    return () => document.removeEventListener('mousedown', fermer);
  }, [ouvert]);

  const solder = async (mode) => {
    setOuvert(false);
    setEnCours(true);
    // Montants figés AVANT le solde : après, les lignes ne sont plus impayées.
    const avant = impayees.map((l) => ({
      id: l.id,
      montant: Number((decompte(l).montant - dejaCouvert(l)).toFixed(3)),
    }));
    const { error, soldees, total: t } = await solderCamion(reception.id, { mode, utilisateur, compteId: caisseImposee(user?.role) ?? undefined });
    setEnCours(false);
    if (error) {
      toast.error(error);
      onDone?.();
      return;
    }
    toast.success(`Camion ${reception.bonParent || reception.id.split('/')[0]} soldé : ${soldees.length} lignes, ${fmt(t, 3)} TND (${mode})`);
    setRecu({
      bon: reception.bonParent || reception.id.split('/')[0],
      client: reception.parcours === 'client',
      tiers: nomTiers(reception.tiersId),
      date: new Date().toISOString().slice(0, 10),
      mode,
      total: Number(t),
      lignes: avant.filter((l) => soldees.includes(l.id)),
    });
    // `onDone` recharge la liste et démonte ce bouton (plus aucune ligne impayée) :
    // on diffère le rafraîchissement à la fermeture du reçu.
  };

  if (recu)
    return (
      <PrintDoc
        type={recu.client ? 'Reçu d’encaissement — camion' : 'Reçu de paiement — camion'}
        numero={attribuerNumero('recu_paiement', `camion:${recu.bon}:${recu.date}:${recu.total}`)}
        date={recu.date}
        tiers={recu.tiers}
        lignes={[
          [
            'Nature de l’opération',
            recu.client
              ? `Encaissement groupé des prestations du camion ${recu.bon}`
              : `Paiement groupé des achats du camion ${recu.bon}`,
          ],
          ['Mode de règlement', recu.mode],
          ['Lignes soldées', `${recu.lignes.length} ligne(s)`],
          ...recu.lignes.map((l) => [`Ligne ${l.id}`, `${fmt(l.montant, 3)} TND`]),
          ['Notes / Justificatif', `Solde groupé du camion ${recu.bon}`],
        ]}
        totaux={[
          ['Total réglé', `${fmt(recu.total, 3)} TND`],
          ['Nouveau statut du camion', `${recu.lignes.length} ligne(s) soldée(s)`],
        ]}
        mentions="Reçu à signer par les deux parties. Montants exprimés en dinars tunisiens (TND)."
        onClose={() => {
          setRecu(null);
          onDone?.();
        }}
      />
    );

  if (impayees.length < 2) return null;

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        data-testid={`solder-camion-${reception.id}`}
        disabled={enCours || tresorerieKo}
        onClick={() => solder('Espèces')}
        title={titreVerrou ?? `Solder les ${impayees.length} lignes du camion : ${fmt(total, 3)} TND en espèces`}
        className="inline-flex h-8 items-center gap-1.5 border-2 border-emerald-700 px-2.5 text-xs font-bold text-emerald-800 transition-colors duration-150 hover:bg-emerald-700 hover:text-white disabled:opacity-60"
      >
        <Banknote size={13} /> Camion ({impayees.length}) · {fmt(total, 3)}
      </button>
      <button
        type="button"
        data-testid={`solder-camion-menu-${reception.id}`}
        onClick={() => setOuvert((o) => !o)}
        className="inline-flex h-8 items-center border-2 border-l-0 border-emerald-700 px-1 text-emerald-800 transition-colors duration-150 hover:bg-emerald-700 hover:text-white"
      >
        <ChevronDown size={13} />
      </button>
      {ouvert && (
        <div className="absolute right-0 top-full z-20 mt-1 min-w-[150px] border border-slate-200 bg-white shadow-lg">
          {MODES_PAIEMENT.map((m) => (
            <button key={m} type="button" data-testid={`solder-camion-mode-${m}-${reception.id}`} onClick={() => solder(m)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-900 hover:text-white">
              <CheckCircle2 size={12} /> {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// « Solder » en un clic (espèces) + petit menu pour un autre mode de paiement.
export const SolderButton = ({ reception, montant, utilisateur, onDone }) => {
  const { user } = useAuth();
  const { verrou: tresorerieKo, titre: titreVerrou } = useTresorerieVerrou();
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [recu, setRecu] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!ouvert) return;
    const fermer = (e) => ref.current && !ref.current.contains(e.target) && setOuvert(false);
    document.addEventListener('mousedown', fermer);
    return () => document.removeEventListener('mousedown', fermer);
  }, [ouvert]);

  const solder = async (mode) => {
    setOuvert(false);
    setEnCours(true);
    const { error, montant: regle, mode: modeRegle } = await solderReception(reception.id, { mode, utilisateur, compteId: caisseImposee(user?.role) ?? undefined });
    setEnCours(false);
    if (error) return toast.error(error);
    toast.success(`${reception.id} soldée : ${fmt(regle, 3)} TND (${modeRegle})`);
    setRecu({
      bon: reception.id,
      client: reception.parcours === 'client',
      tiers: nomTiers(reception.tiersId),
      date: new Date().toISOString().slice(0, 10),
      montant: Number(regle),
      mode: modeRegle,
    });
    // `onDone` recharge la liste et démonte ce bouton (la ligne n'est plus impayée) :
    // on diffère le rafraîchissement à la fermeture du reçu.
  };

  if (recu)
    return (
      <PrintDoc
        type={recu.client ? 'Reçu d’encaissement — réception' : 'Reçu de paiement — réception'}
        numero={attribuerNumero('recu_paiement', `reception:${recu.bon}`)}
        date={recu.date}
        tiers={recu.tiers}
        lignes={[
          [
            'Nature de l’opération',
            recu.client
              ? `Encaissement reçu du client pour la prestation ${recu.bon}`
              : `Paiement versé par l’huilerie pour l’achat ${recu.bon}`,
          ],
          ['Mode de règlement', recu.mode],
          ['Notes / Justificatif', `Solde de la ligne ${recu.bon}`],
        ]}
        totaux={[
          ['Montant réglé', `${fmt(recu.montant, 3)} TND`],
          ['Nouveau statut de la ligne', 'Soldée'],
        ]}
        mentions="Reçu à signer par les deux parties. Montants exprimés en dinars tunisiens (TND)."
        onClose={() => {
          setRecu(null);
          onDone?.();
        }}
      />
    );

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        data-testid={`solder-${reception.id}`}
        disabled={enCours || tresorerieKo}
        onClick={() => solder('Espèces')}
        title={titreVerrou ?? `Solder ${fmt(montant, 3)} TND en espèces`}
        className="inline-flex h-8 items-center gap-1.5 bg-emerald-700 px-2.5 text-xs font-bold text-white transition-colors duration-150 hover:bg-emerald-800 disabled:opacity-60"
      >
        <Banknote size={13} /> Solder
      </button>
      <button
        type="button"
        data-testid={`solder-menu-${reception.id}`}
        onClick={() => setOuvert((o) => !o)}
        title="Autre mode de paiement"
        className="inline-flex h-8 items-center border-l border-emerald-900/30 bg-emerald-700 px-1 text-white transition-colors duration-150 hover:bg-emerald-800"
      >
        <ChevronDown size={13} />
      </button>
      {ouvert && (
        <div data-testid={`solder-modes-${reception.id}`} className="absolute right-0 top-full z-20 mt-1 min-w-[150px] border border-slate-200 bg-white shadow-lg">
          {MODES_PAIEMENT.map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`solder-mode-${m}-${reception.id}`}
              onClick={() => solder(m)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <CheckCircle2 size={12} /> {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
